/**
 * Sanity Gallery Service
 * 
 * Provides CRUD operations to sync Gallery data between the admin dashboard
 * and Sanity CMS. This service handles the mapping between the local Supabase
 * gallery schema and the Sanity gallery document type.
 * 
 * @version 1.0.0
 * @author Claude Code
 */

import { Gallery } from '@/lib/supabase';
import { SanityMcpClient } from '@/lib/api/sanityMcpClient';

// Sanity Gallery Document Type Interface
// Based on the Sanity schema analysis
export interface SanityGalleryDocument {
  _id?: string;
  _type: 'gallery';
  _createdAt?: string;
  _updatedAt?: string;
  title: string;
  slug: {
    current: string;
    _type: 'slug';
  };
  description: string;
  image: {
    _type: 'image';
    asset: {
      _ref: string;
      _type: 'reference';
    };
    hotspot?: any;
    crop?: any;
    alt: string;
    caption?: string;
  };
  additionalImages?: Array<{
    _type: 'mediaItem';
    image: {
      _type: 'image';
      asset: {
        _ref: string;
        _type: 'reference';
      };
      alt: string;
      caption?: string;
    };
  }>;
  category: 'flagstone' | 'brick' | 'stone' | 'materials' | 'techniques' | 'before-after' | 
           'heritage' | 'commercial' | 'residential' | 'chimney' | 'foundation' | 'tools' | 
           'seasonal' | 'problems' | 'solutions';
  secondaryCategories?: string[];
  tags?: string[];
  workType?: 'new-installation' | 'repair' | 'restoration' | 'maintenance' | 'emergency' | 
            'enhancement' | 'material-sample' | 'technique-demo' | 'problem-example' | 'solution-example';
  difficulty?: 'basic' | 'intermediate' | 'advanced' | 'expert';
  relatedService?: {
    _ref: string;
    _type: 'reference';
  };
  relatedProject?: {
    _ref: string;
    _type: 'reference';
  };
  location?: {
    _ref: string;
    _type: 'reference';
  };
  locationDetails?: string;
  featured?: boolean;
  displayOrder?: number;
  publishingStatus: 'draft' | 'review' | 'published' | 'featured' | 'archived';
  seoKeywords?: string[];
  datePhotographed?: string; // YYYY-MM-DD format
  photographer?: string;
  notes?: string;
}

// Mapping utilities between local Gallery and Sanity Gallery
export class SanityGalleryMapper {
  /**
   * Maps a local Gallery to a Sanity gallery document
   */
  static toSanityDocument(localGallery: Gallery, coverImageAssetRef?: string): Omit<SanityGalleryDocument, '_id' | '_createdAt' | '_updatedAt'> {
    // Generate slug from title
    const slug = localGallery.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Map franchise regions to Sanity categories
    const categoryMap: Record<string, SanityGalleryDocument['category']> = {
      'HQ': 'commercial',
      'hamilton': 'residential',
      'north_york': 'residential',
      'toronto': 'residential',
      'london': 'residential',
      'general': 'residential'
    };

    // Map review status to publishing status
    const publishingStatusMap: Record<string, SanityGalleryDocument['publishingStatus']> = {
      'pending': 'draft',
      'approved': 'published',
      'rejected': 'archived'
    };

      return {
        _type: 'gallery',
        title: localGallery.title,
      slug: {
        current: slug,
        _type: 'slug'
      },
      description: localGallery.description || `Gallery showcasing ${localGallery.title}`,
      // Main image - requires asset reference from uploaded image
      image: {
        _type: 'image',
        asset: {
          _ref: coverImageAssetRef || '', // This needs to be populated with actual Sanity asset reference
          _type: 'reference'
        },
        alt: `${localGallery.title} - Cover Image`,
        caption: localGallery.description || ''
        },
        category: categoryMap[localGallery.franchise_region || 'general'] || 'residential',
        tags: [localGallery.franchise_region],
        featured: false, // Can be set based on business rules
        displayOrder: 1, // Default order, can be updated
        publishingStatus: publishingStatusMap[localGallery.review_status] || 'draft',
      locationDetails: localGallery.franchise_region,
      datePhotographed: localGallery.created_on.split('T')[0], // Convert to YYYY-MM-DD
      notes: `Imported from admin dashboard gallery ID: ${localGallery.id}`
    };
  }

  /**
     * Maps a Sanity gallery document back to local Gallery format
     */
    static toLocalGallery(sanityDoc: SanityGalleryDocument): Partial<Gallery> {
      // Map publishing status back to review status
      const reviewStatusMap: Record<SanityGalleryDocument['publishingStatus'], Gallery['review_status']> = {
        'draft': 'pending',
        'review': 'pending', 
        'published': 'approved',
        'featured': 'approved',
        'archived': 'rejected'
      };

      const allowedRegions = [
        'HQ',
        'north_york',
        'london',
        'toronto',
        'hamilton',
        'mississauga',
        'brampton',
        'scarborough',
        'general'
      ] as const

      const franchiseRegion = allowedRegions.includes(sanityDoc.locationDetails as any)
        ? (sanityDoc.locationDetails as Gallery['franchise_region'])
        : 'general'
  
      return {
        title: sanityDoc.title,
        description: sanityDoc.description,
        review_status: reviewStatusMap[sanityDoc.publishingStatus] || 'pending',
        franchise_region: franchiseRegion,
        // Note: cover_image would need to be resolved from Sanity asset reference
        // is_public and used_for_social_media would be derived from publishingStatus
        is_public: sanityDoc.publishingStatus === 'published' || sanityDoc.publishingStatus === 'featured',
      used_for_social_media: sanityDoc.featured || false
    };
  }
}

/**
 * Sanity Gallery Service
 * 
 * Provides CRUD operations for syncing galleries with Sanity CMS
 */
export class SanityGalleryService {
  private static SANITY_PROJECT_ID = '2jg18uur';
  private static SANITY_DATASET = 'production';

  /**
   * Creates a new gallery document in Sanity
   */
  static async createGallery(localGallery: Gallery, coverImageUrl?: string): Promise<string> {
    try {
      const sanityDocument = SanityGalleryMapper.toSanityDocument(localGallery);
      
      const instruction = `Create a gallery document with the following details:
        - Title: "${sanityDocument.title}"
        - Description: "${sanityDocument.description}"
        - Category: "${sanityDocument.category}"
        - Publishing Status: "${sanityDocument.publishingStatus}"
        - Location Details: "${sanityDocument.locationDetails || 'Not specified'}"
        - Slug: "${sanityDocument.slug.current}"
        ${coverImageUrl ? `- Cover Image URL: "${coverImageUrl}" (please create an image asset from this URL)` : ''}
        - Date Photographed: "${sanityDocument.datePhotographed}"
        ${sanityDocument.notes ? `- Notes: "${sanityDocument.notes}"` : ''}
      
      Please create this as a gallery document type in Sanity CMS.`;

      // Use the MCP client to create the document
      const result = await SanityMcpClient.createDocument('gallery', [instruction]);
      
      return result._id;
    } catch (error) {
      console.error('Error creating Sanity gallery:', error);
      throw new Error(`Failed to create gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Updates an existing gallery document in Sanity
   */
  static async updateGallery(sanityId: string, localGallery: Gallery): Promise<void> {
    try {
      const sanityDocument = SanityGalleryMapper.toSanityDocument(localGallery);
      
      const instruction = `Update the gallery with these details:
        - Title: "${sanityDocument.title}"
        - Description: "${sanityDocument.description}"
        - Publishing Status: "${sanityDocument.publishingStatus}"
        - Location Details: "${sanityDocument.locationDetails || 'Not specified'}"
        - Date Photographed: "${sanityDocument.datePhotographed}"
        ${sanityDocument.notes ? `- Notes: "${sanityDocument.notes}"` : ''}`;
      
      await SanityMcpClient.updateDocument(sanityId, instruction);
    } catch (error) {
      console.error('Error updating Sanity gallery:', error);
      throw new Error(`Failed to update gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves a gallery document from Sanity by ID
   */
  static async getGallery(sanityId: string): Promise<SanityGalleryDocument | null> {
    try {
      return await SanityMcpClient.getDocument<SanityGalleryDocument>(sanityId);
    } catch (error) {
      console.error('Error fetching Sanity gallery:', error);
      return null;
    }
  }

  /**
   * Searches for galleries in Sanity by title or slug
   */
  static async searchGalleries(searchTerm: string): Promise<SanityGalleryDocument[]> {
    try {
      return await SanityMcpClient.searchGalleries(searchTerm);
    } catch (error) {
      console.error('Error searching Sanity galleries:', error);
      return [];
    }
  }

  /**
   * Deletes a gallery document from Sanity
   */
  static async deleteGallery(sanityId: string): Promise<void> {
    try {
      await SanityMcpClient.deleteDocument(sanityId);
    } catch (error) {
      console.error('Error deleting Sanity gallery:', error);
      throw new Error(`Failed to delete gallery from Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Publishes a gallery document in Sanity
   */
  static async publishGallery(sanityId: string): Promise<void> {
    try {
      await SanityMcpClient.patchDocument(sanityId, {
        op: 'set',
        path: 'publishingStatus',
        value: 'published'
      });
    } catch (error) {
      console.error('Error publishing Sanity gallery:', error);
      throw new Error(`Failed to publish gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Archives a gallery document in Sanity
   */
  static async archiveGallery(sanityId: string): Promise<void> {
    try {
      await SanityMcpClient.patchDocument(sanityId, {
        op: 'set',
        path: 'publishingStatus',
        value: 'archived'
      });
    } catch (error) {
      console.error('Error archiving Sanity gallery:', error);
      throw new Error(`Failed to archive gallery in Sanity: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Export the service as default

export default SanityGalleryService;
